import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Loader2, RefreshCw, Search, ArrowUpDown, Settings, ArrowRight, Check, X, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function AgentsReports() {
    const [tables, setTables] = useState([]);
    const [comparing, setComparing] = useState(false);

    // Selections
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');

    // Result Data
    const [result, setResult] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [agentData, setAgentData] = useState(null);
    const [lostTransferredData, setLostTransferredData] = useState(null);

    // Filter & Ranking Data
    const [rankings, setRankings] = useState([]);
    const [agentSearch, setAgentSearch] = useState(''); // Unified Search

    // Filtering & Sorting for Agent Table
    const [sortConfig, setSortConfig] = useState({ key: 'agent', direction: 'asc' });
    const [rankSortConfig, setRankSortConfig] = useState({ key: 'rank_sales_p2', direction: 'asc' });

    // New Features: Filtering & Resizing
    const [columnFilters, setColumnFilters] = useState({});
    const [columnWidths, setColumnWidths] = useState({
        agent: 250,
        clients_p1: 120,
        sales_p1: 140,
        clients_p2: 120,
        sales_p2: 140,
        new_count: 100,
        new_value: 120,
        lost_count: 100,
        lost_value: 120,
        // Share Columns
        share_sales_p1: 100,
        share_sales_p2: 100,
        share_profit_p1: 100,
        share_profit_p2: 100,
        share_clients_p1: 100,
        share_clients_p2: 100,
        share_new_clients: 100,
        share_lost_clients: 100,
        // Rank Widths
        rank_sales_p1: 100,
        rank_sales_p2: 100,
        rank_profit_p1: 100,
        rank_profit_p2: 100,
        rank_margin_p1: 100,
        rank_margin_p2: 100,
        rank_clients_p1: 100,
        rank_clients_p2: 100,
        rank_new: 100,
        rank_lost: 100
    });

    // Column Visibility State
    const [visibleColumns, setVisibleColumns] = useState({
        agent: true,
        clients_p1: true,
        sales_p1: true,
        clients_p2: true,
        sales_p2: true,
        new_count: true,
        new_value: true,
        lost_count: true,
        lost_value: true,
        // Share Columns
        share_sales_p1: true,
        share_sales_p2: true,
        share_profit_p1: true,
        share_profit_p2: true,
        share_clients_p1: true,
        share_clients_p2: true,
        share_new_clients: true,
        share_lost_clients: true,
        // Rank Columns
        rank_sales_p1: true,
        rank_sales_p2: true,
        rank_profit_p1: true,
        rank_profit_p2: true,
        rank_margin_p1: true,
        rank_margin_p2: true,
        rank_clients_p1: true,
        rank_clients_p2: true,
        rank_new: true,
        rank_lost: true
    });

    const columnKeys = [
        { key: 'agent', label: 'Agent' },
        { key: 'clients_p1', label: result ? `Nr. Clienti ${result.period1.name}` : 'Nr. Clienti P1' },
        { key: 'sales_p1', label: result ? `Total CA ${result.period1.name}` : 'Total CA P1' },
        { key: 'clients_p2', label: result ? `Nr. Clienti ${result.period2.name}` : 'Nr. Clienti P2' },
        { key: 'sales_p2', label: result ? `Total CA ${result.period2.name}` : 'Total CA P2' },
        { key: 'new_count', label: 'Clienti Noi' },
        { key: 'new_value', label: 'CA Clienti Noi' },
        { key: 'lost_count', label: 'Clienti Pierduti' },
        { key: 'lost_value', label: 'CA Clienti Pierduti' },
        { key: 'share_sales_p1', label: 'Pondere CA 2024' },
        { key: 'share_sales_p2', label: 'Pondere CA 2025' },
        { key: 'share_profit_p1', label: 'Pondere din profit 2024' },
        { key: 'share_profit_p2', label: 'Pondere din profit 2025' },
        { key: 'share_clients_p1', label: 'Pondere Nr. Clienti 2024' },
        { key: 'share_clients_p2', label: 'Pondere Nr. Clienti 2025' },
        { key: 'share_new_clients', label: 'Pondere clienti noi 2025' },
        { key: 'share_lost_clients', label: 'Pondere Clienti Pierduti 2025' }
    ];

    const rankColumnKeys = [
        { key: 'agent', label: 'Agent' },
        { key: 'rank_sales_p1', label: result ? `Top Vanzari ${result.period1.name}` : 'Top Vanzari P1' },
        { key: 'rank_sales_p2', label: result ? `Top Vanzari ${result.period2.name}` : 'Top Vanzari P2' },
        { key: 'rank_profit_p1', label: result ? `Top Profit ${result.period1.name}` : 'Top Profit P1' },
        { key: 'rank_profit_p2', label: result ? `Top Profit ${result.period2.name}` : 'Top Profit P2' },
        { key: 'rank_margin_p1', label: result ? `Top Marja ${result.period1.name}` : 'Top Marja P1' },
        { key: 'rank_margin_p2', label: result ? `Top Marja ${result.period2.name}` : 'Top Marja P2' },
        { key: 'rank_clients_p1', label: result ? `Top Clienti ${result.period1.name}` : 'Top Clienti P1' },
        { key: 'rank_clients_p2', label: result ? `Top Clienti ${result.period2.name}` : 'Top Clienti P2' },
        { key: 'rank_new', label: 'Top Clienti Noi' },
        { key: 'rank_lost', label: 'Top Clienti Pierduti' }
    ];

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data || []);
        } catch (e) {
            console.error(e);
            toast.error("Nu am putut incarca lista de fisiere.");
        }
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            toast.warning("Te rog selecteaza ambele fisiere pentru comparatie.");
            return;
        }

        setComparing(true);
        setResult(null);
        setClientData(null);
        setAgentData(null);
        setRankings([]);

        try {
            // Parallel Fetch
            const [resTotals, resClients, resAgents, resDetailed] = await Promise.all([
                axios.post(`${API}/reports/compare-totals`, { table1, table2 }),
                axios.post(`${API}/reports/compare-clients`, { table1, table2 }),
                axios.post(`${API}/reports/compare-agents`, { table1, table2 }),
                axios.post(`${API}/reports/detailed-clients-comparison`, { table1, table2 })
            ]);

            setResult(resTotals.data);
            setClientData(resClients.data);
            setAgentData(resAgents.data);
            setLostTransferredData(resDetailed.data);

            // Compute Rankings
            const computedRanks = computeRankings(resAgents.data);
            setRankings(computedRanks);

            toast.success("Comparatie realizata cu succes!");
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.detail || "Eroare la compararea fisierelor.");
        } finally {
            setComparing(false);
        }
    };

    const computeRankings = (agents) => {
        if (!agents || agents.length === 0) return [];

        // Helper to get rank map: { agentName: rank }
        const getRankMap = (key, desc = true) => {
            const sorted = [...agents].sort((a, b) => desc ? b[key] - a[key] : a[key] - b[key]);
            const map = {};
            sorted.forEach((agent, index) => {
                map[agent.agent] = index + 1;
            });
            return map;
        };

        // Ranks Calculation
        const rankSalesP1 = getRankMap('sales_p1');
        const rankSalesP2 = getRankMap('sales_p2');

        const rankProfitP1 = getRankMap('profit_p1');
        const rankProfitP2 = getRankMap('profit_p2');

        const rankMarginP1 = getRankMap('margin_p1');
        const rankMarginP2 = getRankMap('margin_p2');

        const rankClientsP1 = getRankMap('clients_p1');
        const rankClientsP2 = getRankMap('clients_p2');

        const rankNewClients = getRankMap('new_count');
        const rankLostClients = getRankMap('lost_count'); // Usually highest lost is "bad", but rank 1 means "most lost"

        return agents.map(a => ({
            ...a,
            rank_sales_p1: rankSalesP1[a.agent],
            rank_sales_p2: rankSalesP2[a.agent],
            rank_profit_p1: rankProfitP1[a.agent],
            rank_profit_p2: rankProfitP2[a.agent],
            rank_margin_p1: rankMarginP1[a.agent],
            rank_margin_p2: rankMarginP2[a.agent],
            rank_clients_p1: rankClientsP1[a.agent],
            rank_clients_p2: rankClientsP2[a.agent],
            rank_new: rankNewClients[a.agent],
            rank_lost: rankLostClients[a.agent]
        })).sort((a, b) => a.rank_sales_p2 - b.rank_sales_p2); // Default sort by Current Sales Rank
    };

    const formatMoney = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatPercent = (val) => {
        if (!val || isNaN(val)) return "0.00%";
        let p = val * 100;
        if (Math.abs(p) < 0.005) p = 0; // Fix -0.00
        return p.toFixed(2) + "%";
    };

    // Resizing Logic
    const handleResizeMouseDown = (e, key) => {
        e.preventDefault();
        const startX = e.pageX;
        const startWidth = columnWidths[key] || 100;

        const onMouseMove = (moveEvent) => {
            const currentX = moveEvent.pageX;
            const diff = currentX - startX;
            setColumnWidths(prev => ({
                ...prev,
                [key]: Math.max(50, startWidth + diff)
            }));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Filter Logic
    const handleFilterChange = (key, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Agent Table Logic
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedAgents = () => {
        if (!agentData) return [];

        let filtered = agentData;

        // Global Search
        if (agentSearch) {
            const q = agentSearch.toLowerCase();
            filtered = filtered.filter(a => (a.agent || '').toLowerCase().includes(q));
        }

        // Column Filters
        const filterKeys = Object.keys(columnFilters);
        if (filterKeys.length > 0) {
            filtered = filtered.filter(row => {
                return filterKeys.every(key => {
                    const filterVal = columnFilters[key].toLowerCase();
                    if (!filterVal) return true;

                    let rowVal = row[key];
                    if (rowVal === null || rowVal === undefined) rowVal = '';
                    return String(rowVal).toLowerCase().includes(filterVal);
                });
            });
        }

        return [...filtered].sort((a, b) => {
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];

            // String vs Number sort
            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc'
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            } else {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
        });
    };

    // Rankings Table Filtering
    const getFilteredRankings = () => {
        if (!rankings) return [];
        if (!agentSearch) return rankings;
        const q = agentSearch.toLowerCase();
        return rankings.filter(a => (a.agent || '').toLowerCase().includes(q));
    };

    const sortedAgents = getSortedAgents();
    const filteredRankings = getFilteredRankings();

    // Toggle Column Visibility
    const toggleColumn = (key, value) => {
        setVisibleColumns(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Resizable Header Component
    const ResizableHeader = ({ label, sortKey, widthKey }) => (
        <TableHead
            className="text-black font-bold text-xs uppercase p-0 relative border-r border-gray-300 bg-gray-100 select-none group align-top h-auto"
            style={{ width: columnWidths[widthKey], maxWidth: columnWidths[widthKey], minWidth: columnWidths[widthKey] }}
        >
            <div className="flex flex-col h-full justify-between">
                {/* Header Top: Label & Sort */}
                <div
                    className="flex items-start justify-between p-2 cursor-pointer hover:bg-black/5 flex-1"
                    onClick={() => handleSort(sortKey)}
                >
                    <span className="mr-1 whitespace-normal break-words leading-tight" title={label}>{label}</span>
                    <ArrowUpDown className="h-3 w-3 text-gray-500 shrink-0 mt-1" />
                </div>

                {/* Header Bottom: Filter Input */}
                <div className="px-1 pb-1">
                    <input
                        className="w-full text-[10px] p-1 border border-gray-300 rounded text-black bg-white focus:outline-none focus:border-blue-500"
                        placeholder="Cauta..."
                        value={columnFilters[sortKey] || ''}
                        onChange={(e) => handleFilterChange(sortKey, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>

            {/* Resize Handle */}
            <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 z-10"
                onMouseDown={(e) => handleResizeMouseDown(e, widthKey)}
            />
        </TableHead>
    );

    // Ranking Table Sort Logic
    const handleRankSort = (key) => {
        let direction = 'asc';
        if (rankSortConfig.key === key && rankSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setRankSortConfig({ key, direction });
    };

    const getSortedRankings = (rankData) => {
        if (!rankData) return [];
        return [...rankData].sort((a, b) => {
            const valA = a[rankSortConfig.key];
            const valB = b[rankSortConfig.key];

            // For ranks, usually lower is better (1 is best), but standard sort:
            // Ascending: 1, 2, 3...
            // Descending: 10, 9, 8...

            if (typeof valA === 'string') {
                return rankSortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return rankSortConfig.direction === 'asc' ? valA - valB : valB - valA;
        });
    };

    const RankSortHeader = ({ label, sortKey, widthKey, align = "center" }) => (
        <TableHead
            className={`text-black font-bold text-xs uppercase cursor-pointer hover:bg-black/10 text-center border-r border-gray-300 align-top py-0 relative group h-auto ${align === 'left' ? 'text-left' : 'text-center'}`}
            style={{ width: columnWidths[widthKey], maxWidth: columnWidths[widthKey], minWidth: columnWidths[widthKey] }}
            onClick={() => handleRankSort(sortKey)}
        >
            <div className={`flex items-center ${align === 'left' ? 'justify-start' : 'justify-center'} space-x-2 h-full p-2`}>
                <span className="whitespace-normal break-words leading-tight">{label}</span>
                <ArrowUpDown className="h-3 w-3 text-gray-500 mt-1 shrink-0" />
            </div>
            {/* Resize Handle */}
            <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 z-10"
                onMouseDown={(e) => { e.stopPropagation(); handleResizeMouseDown(e, widthKey); }}
            />
        </TableHead>
    );

    return (
        <div className="p-8 space-y-8 pb-40">
            {/* Header Navigation */}
            <div className="flex items-center space-x-4">
                <Link to="/reports">
                    <Button variant="ghost" className="text-gray-400 hover:text-white">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi la Rapoarte
                    </Button>
                </Link>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Rapoarte Agenti
                </h1>
            </div>

            {/* Selection Card */}
            <Card className="bg-[#18181b] border-white/10">
                <CardHeader>
                    <CardTitle className="text-gray-300 text-lg">Selectie Perioade de Comparatie</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada Veche (Ex: 2024 / Aprilie)</label>
                        <Select value={table1} onValueChange={setTable1}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white">
                                <SelectValue placeholder="Selecteaza fisier..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada Recenta (Ex: 2025 / Iunie)</label>
                        <Select value={table2} onValueChange={setTable2}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white">
                                <SelectValue placeholder="Selecteaza fisier..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        onClick={handleCompare}
                        disabled={comparing || !table1 || !table2}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                        {comparing ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Genereaza Comparatie
                    </Button>
                </CardContent>
            </Card>

            {/* Results Section */}
            {result && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">



                    {/* TABLE 4: AGENT COMPARISON (Unified Search with Rankings) */}
                    {agentData && (
                        <Card className="bg-[#18181b] border-white/10 overflow-hidden">
                            <div className="bg-yellow-400/90 p-4 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-black uppercase">
                                    Analiză Performanță Agenți
                                </h2>

                                <div className="flex items-center space-x-4">
                                    <div className="relative w-64">
                                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-700" />
                                        <Input
                                            placeholder="Cauta agent..."
                                            className="pl-8 bg-white/90 text-black border-black/20 focus:border-black focus:ring-black placeholder:text-gray-500"
                                            value={agentSearch}
                                            onChange={(e) => setAgentSearch(e.target.value)}
                                        />
                                    </div>
                                    {/* Column Visibility Toggle */}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" size="sm" className="bg-black/10 text-black border-black/20 hover:bg-black/20">
                                                <Settings className="h-4 w-4 mr-2" />
                                                Coloane
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[600px] bg-[#18181b] border-white/20 p-0" align="end">
                                            <div className="grid grid-cols-2 h-[400px]">
                                                {/* Left: Visible Columns */}
                                                <div className="border-r border-white/10">
                                                    <div className="p-3 border-b border-white/10 bg-green-500/10">
                                                        <h3 className="text-green-400 font-semibold flex items-center">
                                                            <Check className="w-4 h-4 mr-2" /> Coloane Afisate
                                                        </h3>
                                                    </div>
                                                    <div className="p-2 space-y-1 h-[340px] overflow-y-auto custom-scrollbar">
                                                        {columnKeys.map(col => visibleColumns[col.key] && (
                                                            <div
                                                                key={col.key}
                                                                className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-green-500/50 cursor-pointer group"
                                                                onClick={() => toggleColumn(col.key, false)}
                                                            >
                                                                <span className="text-sm text-gray-200">{col.label}</span>
                                                                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-green-400" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Right: Hidden Columns */}
                                                <div>
                                                    <div className="p-3 border-b border-white/10 bg-red-500/10">
                                                        <h3 className="text-red-400 font-semibold flex items-center">
                                                            <X className="w-4 h-4 mr-2" /> Coloane Ascunse
                                                        </h3>
                                                    </div>
                                                    <div className="p-2 space-y-1 h-[340px] overflow-y-auto custom-scrollbar">
                                                        {columnKeys.map(col => !visibleColumns[col.key] && (
                                                            <div
                                                                key={col.key}
                                                                className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-red-500/50 cursor-pointer group"
                                                                onClick={() => toggleColumn(col.key, true)}
                                                            >
                                                                <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-red-400" />
                                                                <span className="text-sm text-gray-200">{col.label}</span>
                                                            </div>
                                                        ))}
                                                        {Object.values(visibleColumns).every(v => v) && (
                                                            <div className="text-center text-gray-600 italic py-10">
                                                                Toate coloanele sunt afisate.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader className="bg-gray-100">
                                        <TableRow>
                                            {visibleColumns.agent && <ResizableHeader label="Agent" sortKey="agent" widthKey="agent" />}
                                            {visibleColumns.clients_p1 && <ResizableHeader label={result ? `Nr. Clienti ${result.period1.name}` : 'Nr. Clienti P1'} sortKey="clients_p1" widthKey="clients_p1" />}
                                            {visibleColumns.sales_p1 && <ResizableHeader label={result ? `Total CA ${result.period1.name}` : 'Total CA P1'} sortKey="sales_p1" widthKey="sales_p1" />}
                                            {visibleColumns.clients_p2 && <ResizableHeader label={result ? `Nr. Clienti ${result.period2.name}` : 'Nr. Clienti P2'} sortKey="clients_p2" widthKey="clients_p2" />}
                                            {visibleColumns.sales_p2 && <ResizableHeader label={result ? `Total CA ${result.period2.name}` : 'Total CA P2'} sortKey="sales_p2" widthKey="sales_p2" />}
                                            {visibleColumns.new_count && <ResizableHeader label="Clienti Noi" sortKey="new_count" widthKey="new_count" />}
                                            {visibleColumns.new_value && <ResizableHeader label="CA Clienti Noi" sortKey="new_value" widthKey="new_value" />}
                                            {visibleColumns.lost_count && <ResizableHeader label="Clienti Pierduti" sortKey="lost_count" widthKey="lost_count" />}
                                            {visibleColumns.lost_value && <ResizableHeader label="CA Clienti Pierduti" sortKey="lost_value" widthKey="lost_value" />}
                                            {visibleColumns.share_sales_p1 && <ResizableHeader label="Pondere CA 2024" sortKey="share_sales_p1" widthKey="share_sales_p1" />}
                                            {visibleColumns.share_sales_p2 && <ResizableHeader label="Pondere CA 2025" sortKey="share_sales_p2" widthKey="share_sales_p2" />}
                                            {visibleColumns.share_profit_p1 && <ResizableHeader label="Pondere Profit 2024" sortKey="share_profit_p1" widthKey="share_profit_p1" />}
                                            {visibleColumns.share_profit_p2 && <ResizableHeader label="Pondere Profit 2025" sortKey="share_profit_p2" widthKey="share_profit_p2" />}
                                            {visibleColumns.share_clients_p1 && <ResizableHeader label="Pondere Clienti 2024" sortKey="share_clients_p1" widthKey="share_clients_p1" />}
                                            {visibleColumns.share_clients_p2 && <ResizableHeader label="Pondere Clienti 2025" sortKey="share_clients_p2" widthKey="share_clients_p2" />}
                                            {visibleColumns.share_new_clients && <ResizableHeader label="Pondere Clienti Noi" sortKey="share_new_clients" widthKey="share_new_clients" />}
                                            {visibleColumns.share_lost_clients && <ResizableHeader label="Pondere Clienti Pierduti" sortKey="share_lost_clients" widthKey="share_lost_clients" />}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortedAgents.map((agent, i) => (
                                            <TableRow key={i} className="border-white/10 hover:bg-white/5">
                                                {visibleColumns.agent && <TableCell className="text-gray-300 font-medium p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.agent }}>{agent.agent || "Fara Agent"}</TableCell>}
                                                {visibleColumns.clients_p1 && <TableCell className="text-right text-white font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.clients_p1 }}>{agent.clients_p1}</TableCell>}
                                                {visibleColumns.sales_p1 && <TableCell className="text-right text-yellow-400 font-mono p-1 text-sm border-r border-white/10 font-bold truncate" style={{ maxWidth: columnWidths.sales_p1 }}>{formatMoney(agent.sales_p1)}</TableCell>}
                                                {visibleColumns.clients_p2 && <TableCell className="text-right text-white font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.clients_p2 }}>{agent.clients_p2}</TableCell>}
                                                {visibleColumns.sales_p2 && <TableCell className="text-right text-yellow-400 font-mono p-1 text-sm border-r border-white/10 font-bold truncate" style={{ maxWidth: columnWidths.sales_p2 }}>{formatMoney(agent.sales_p2)}</TableCell>}
                                                {visibleColumns.new_count && <TableCell className="text-right text-green-400 font-mono font-bold p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.new_count }}>+{agent.new_count}</TableCell>}
                                                {visibleColumns.new_value && <TableCell className="text-right text-white font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.new_value }}>{formatMoney(agent.new_value)}</TableCell>}
                                                {visibleColumns.lost_count && <TableCell className="text-right text-red-400 font-mono font-bold p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.lost_count }}>-{agent.lost_count}</TableCell>}
                                                {visibleColumns.lost_value && <TableCell className="text-right text-white font-mono p-1 text-sm truncate border-r border-white/10" style={{ maxWidth: columnWidths.lost_value }}>{formatMoney(agent.lost_value)}</TableCell>}
                                                {visibleColumns.share_sales_p1 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_sales_p1 }}>{formatPercent(agent.share_sales_p1)}</TableCell>}
                                                {visibleColumns.share_sales_p2 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_sales_p2 }}>{formatPercent(agent.share_sales_p2)}</TableCell>}
                                                {visibleColumns.share_profit_p1 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_profit_p1 }}>{formatPercent(agent.share_profit_p1)}</TableCell>}
                                                {visibleColumns.share_profit_p2 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_profit_p2 }}>{formatPercent(agent.share_profit_p2)}</TableCell>}
                                                {visibleColumns.share_clients_p1 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_clients_p1 }}>{formatPercent(agent.share_clients_p1)}</TableCell>}
                                                {visibleColumns.share_clients_p2 && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_clients_p2 }}>{formatPercent(agent.share_clients_p2)}</TableCell>}
                                                {visibleColumns.share_new_clients && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm border-r border-white/10 truncate" style={{ maxWidth: columnWidths.share_new_clients }}>{formatPercent(agent.share_new_clients)}</TableCell>}
                                                {visibleColumns.share_lost_clients && <TableCell className="text-right text-gray-300 font-mono p-1 text-sm truncate" style={{ maxWidth: columnWidths.share_lost_clients }}>{formatPercent(agent.share_lost_clients)}</TableCell>}
                                            </TableRow>
                                        ))}
                                        {sortedAgents.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={columnKeys.filter(k => visibleColumns[k.key]).length} className="text-center text-gray-500 py-8">
                                                    Niciun agent gasit pentru filtrele curente.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {/* Total Row */}
                                        <TableRow className="bg-white/10 border-t border-white/20 font-bold">
                                            {visibleColumns.agent && <TableCell className="text-white p-2">TOTAL</TableCell>}
                                            {visibleColumns.clients_p1 && <TableCell className="text-right text-white font-mono p-2">{sortedAgents.reduce((sum, a) => sum + a.clients_p1, 0)}</TableCell>}
                                            {visibleColumns.sales_p1 && <TableCell className="text-right text-yellow-400 font-mono p-2 font-bold">{formatMoney(sortedAgents.reduce((sum, a) => sum + (a.sales_p1 || 0), 0))}</TableCell>}
                                            {visibleColumns.clients_p2 && <TableCell className="text-right text-white font-mono p-2">{sortedAgents.reduce((sum, a) => sum + a.clients_p2, 0)}</TableCell>}
                                            {visibleColumns.sales_p2 && <TableCell className="text-right text-yellow-400 font-mono p-2 font-bold">{formatMoney(sortedAgents.reduce((sum, a) => sum + (a.sales_p2 || 0), 0))}</TableCell>}
                                            {visibleColumns.new_count && <TableCell className="text-right text-green-400 font-mono p-2">{sortedAgents.reduce((sum, a) => sum + a.new_count, 0)}</TableCell>}
                                            {visibleColumns.new_value && <TableCell className="text-right text-white font-mono p-2">{formatMoney(sortedAgents.reduce((sum, a) => sum + a.new_value, 0))}</TableCell>}
                                            {visibleColumns.lost_count && <TableCell className="text-right text-red-400 font-mono p-2">{sortedAgents.reduce((sum, a) => sum + a.lost_count, 0)}</TableCell>}
                                            {visibleColumns.lost_value && <TableCell className="text-right text-white font-mono p-2">{formatMoney(sortedAgents.reduce((sum, a) => sum + a.lost_value, 0))}</TableCell>}
                                            {visibleColumns.share_sales_p1 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_sales_p2 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_profit_p1 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_profit_p2 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_clients_p1 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_clients_p2 && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_new_clients && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                            {visibleColumns.share_lost_clients && <TableCell className="text-right text-gray-300 font-mono p-2 font-bold">100.00%</TableCell>}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* TABLE 5: TOP RANKINGS (Moved to Bottom) */}
                    {filteredRankings.length > 0 && (
                        <Card className="bg-[#18181b] border-white/10 overflow-hidden">
                            <div className="bg-yellow-400/90 p-4 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-black uppercase">
                                    Top Agenti vanzari {/* Renamed Title per request */}
                                </h2>
                                <div className="flex items-center space-x-4">
                                    <div className="relative w-64">
                                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-700" />
                                        <Input
                                            placeholder="Cauta agent..."
                                            className="pl-8 bg-white/90 text-black border-black/20 focus:border-black focus:ring-black placeholder:text-gray-500"
                                            value={agentSearch}
                                            onChange={(e) => setAgentSearch(e.target.value)}
                                        />
                                    </div>
                                    {/* Rank Column Visibility Toggle */}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" size="sm" className="bg-black/10 text-black border-black/20 hover:bg-black/20">
                                                <Settings className="h-4 w-4 mr-2" />
                                                Coloane
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[600px] bg-[#18181b] border-white/20 p-0" align="end">
                                            <div className="grid grid-cols-2 h-[400px]">
                                                {/* Left: Visible Columns */}
                                                <div className="border-r border-white/10">
                                                    <div className="p-3 border-b border-white/10 bg-green-500/10">
                                                        <h3 className="text-green-400 font-semibold flex items-center">
                                                            <Check className="w-4 h-4 mr-2" /> Coloane Afisate
                                                        </h3>
                                                    </div>
                                                    <div className="p-2 space-y-1 h-[340px] overflow-y-auto custom-scrollbar">
                                                        {rankColumnKeys.map(col => visibleColumns[col.key] && (
                                                            <div
                                                                key={col.key}
                                                                className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-green-500/50 cursor-pointer group"
                                                                onClick={() => toggleColumn(col.key, false)}
                                                            >
                                                                <span className="text-sm text-gray-200">{col.label}</span>
                                                                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-green-400" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Right: Hidden Columns */}
                                                <div>
                                                    <div className="p-3 border-b border-white/10 bg-red-500/10">
                                                        <h3 className="text-red-400 font-semibold flex items-center">
                                                            <X className="w-4 h-4 mr-2" /> Coloane Ascunse
                                                        </h3>
                                                    </div>
                                                    <div className="p-2 space-y-1 h-[340px] overflow-y-auto custom-scrollbar">
                                                        {rankColumnKeys.map(col => !visibleColumns[col.key] && (
                                                            <div
                                                                key={col.key}
                                                                className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-red-500/50 cursor-pointer group"
                                                                onClick={() => toggleColumn(col.key, true)}
                                                            >
                                                                <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-red-400" />
                                                                <span className="text-sm text-gray-200">{col.label}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader className="bg-white text-black">
                                        <TableRow>
                                            {visibleColumns.agent && <RankSortHeader label="Agent" sortKey="agent" widthKey="agent" align="left" />}
                                            {visibleColumns.rank_sales_p1 && <RankSortHeader label={`Top Vanzari ${result.period1.name}`} sortKey="rank_sales_p1" widthKey="rank_sales_p1" />}
                                            {visibleColumns.rank_sales_p2 && <RankSortHeader label={`Top Vanzari ${result.period2.name}`} sortKey="rank_sales_p2" widthKey="rank_sales_p2" />}
                                            {visibleColumns.rank_profit_p1 && <RankSortHeader label={`Top Profit ${result.period1.name}`} sortKey="rank_profit_p1" widthKey="rank_profit_p1" />}
                                            {visibleColumns.rank_profit_p2 && <RankSortHeader label={`Top Profit ${result.period2.name}`} sortKey="rank_profit_p2" widthKey="rank_profit_p2" />}
                                            {visibleColumns.rank_margin_p1 && <RankSortHeader label={`Top Marja ${result.period1.name}`} sortKey="rank_margin_p1" widthKey="rank_margin_p1" />}
                                            {visibleColumns.rank_margin_p2 && <RankSortHeader label={`Top Marja ${result.period2.name}`} sortKey="rank_margin_p2" widthKey="rank_margin_p2" />}
                                            {visibleColumns.rank_clients_p1 && <RankSortHeader label={`Top Clienti ${result.period1.name}`} sortKey="rank_clients_p1" widthKey="rank_clients_p1" />}
                                            {visibleColumns.rank_clients_p2 && <RankSortHeader label={`Top Clienti ${result.period2.name}`} sortKey="rank_clients_p2" widthKey="rank_clients_p2" />}
                                            {visibleColumns.rank_new && <RankSortHeader label="Top Clienti Noi" sortKey="rank_new" widthKey="rank_new" />}
                                            {visibleColumns.rank_lost && <RankSortHeader label="Top Clienti Pierduti" sortKey="rank_lost" widthKey="rank_lost" />}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {getSortedRankings(filteredRankings).map((agent, i) => (
                                            <TableRow key={i} className={`border-white/10 hover:bg-white/5 ${agent.rank_sales_p1 <= 3 ? 'font-bold' : ''}`}>
                                                {visibleColumns.agent && <TableCell className="text-gray-300 font-medium border-r border-white/10 truncate" style={{ maxWidth: columnWidths.agent }}>{agent.agent || "Fara Agent"}</TableCell>}
                                                {visibleColumns.rank_sales_p1 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_sales_p1 <= 3 ? 'text-yellow-400 font-bold bg-white/5' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_sales_p1 }}>{agent.rank_sales_p1}</TableCell>}
                                                {visibleColumns.rank_sales_p2 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_sales_p2 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_sales_p2 }}>{agent.rank_sales_p2}</TableCell>}
                                                {visibleColumns.rank_profit_p1 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_profit_p1 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_profit_p1 }}>{agent.rank_profit_p1}</TableCell>}
                                                {visibleColumns.rank_profit_p2 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_profit_p2 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_profit_p2 }}>{agent.rank_profit_p2}</TableCell>}
                                                {visibleColumns.rank_margin_p1 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_margin_p1 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_margin_p1 }}>{agent.rank_margin_p1}</TableCell>}
                                                {visibleColumns.rank_margin_p2 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_margin_p2 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_margin_p2 }}>{agent.rank_margin_p2}</TableCell>}
                                                {visibleColumns.rank_clients_p1 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_clients_p1 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_clients_p1 }}>{agent.rank_clients_p1}</TableCell>}
                                                {visibleColumns.rank_clients_p2 && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_clients_p2 <= 3 ? 'text-yellow-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_clients_p2 }}>{agent.rank_clients_p2}</TableCell>}
                                                {visibleColumns.rank_new && <TableCell className={`text-center font-mono border-r border-white/10 truncate ${agent.rank_new <= 3 ? 'text-green-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_new }}>{agent.rank_new}</TableCell>}
                                                {visibleColumns.rank_lost && <TableCell className={`text-center font-mono truncate ${agent.rank_lost <= 3 ? 'text-red-400 font-bold' : 'text-white'}`} style={{ maxWidth: columnWidths.rank_lost }}>{agent.rank_lost}</TableCell>}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                </div>
            )}

            {lostTransferredData && <LostTransferredTable data={lostTransferredData} table1={table1} table2={table2} />}

            {agentData && <RetainedClientsTable data={agentData} table1={table1} table2={table2} />}
        </div>
    );
}

function LostTransferredTable({ data, table1, table2 }) {
    const [sortConfig, setSortConfig] = useState({ key: 'sales_diff', direction: 'asc' });
    const [filters, setFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState({
        client: true,
        agent_p1: true,
        agent_p2: true,
        sales_2024: true,
        sales_2025: true,
        sales_diff: true
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const ROWS_PER_PAGE = 15;

    const columns = [
        { key: 'client', label: 'Client' },
        { key: 'agent_p1', label: 'Agent Initial' },
        { key: 'agent_p2', label: 'Agent Final' },
        { key: 'sales_2024', label: 'CA 2024' },
        { key: 'sales_2025', label: 'CA 2025' },
        { key: 'sales_diff', label: 'CA Comparata' }
    ];

    const formatMoney = (val) => {
        if (val === undefined || val === null) return '0.00';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
        setCurrentPage(1); // Reset page on sort
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Reset page on filter
    };

    const getProcessedData = () => {
        let filtered = [...data];

        // Custom Logic: Only show rows where Agent Initial != Agent Final
        // This covers: 
        // 1. New Clients (Agent P1 is null, P2 has value)
        // 2. Lost Clients (Agent P1 has value, P2 is null)
        // 3. Transferred Clients (Agent P1 != Agent P2)
        filtered = filtered.filter(row => row.agent_p1 !== row.agent_p2);


        // Filters
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                const q = filters[key].toLowerCase();
                filtered = filtered.filter(row => String(row[key] || '').toLowerCase().includes(q));
            }
        });

        // Sort
        filtered.sort((a, b) => {
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];

            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            } else {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            }
        });

        return filtered;
    };

    const handleExport = async () => {
        try {
            const response = await axios.post(`${API}/reports/export-lost-transferred`, {
                table1, table2, client_filter: null
            }, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Clienti_Pierduti_Transferati.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error(e);
            toast.error("Eroare la export.");
        }
    };

    const processedData = getProcessedData();

    // Pagination Logic
    const totalPages = Math.ceil(processedData.length / ROWS_PER_PAGE);
    const paginatedData = processedData.slice(
        (currentPage - 1) * ROWS_PER_PAGE,
        currentPage * ROWS_PER_PAGE
    );

    // Totals Calculation
    const totalSales2024 = processedData.reduce((sum, row) => sum + (row.sales_2024 || 0), 0);
    const totalSales2025 = processedData.reduce((sum, row) => sum + (row.sales_2025 || 0), 0);
    const totalSalesDiff = processedData.reduce((sum, row) => sum + (row.sales_diff || 0), 0);

    return (
        <Card className="bg-[#18181b] border-white/10 overflow-hidden mt-8">
            <div className="bg-yellow-400/90 p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-black uppercase">
                    Clienti pierduti/transferati
                </h2>
                <Button onClick={handleExport} size="sm" className="bg-black/80 hover:bg-black text-white border border-white/20">
                    <Download className="w-4 h-4 mr-2" />
                    Export XLSX
                </Button>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader className="bg-gray-100">
                        <TableRow>
                            {columns.map(col => visibleColumns[col.key] && (
                                <TableHead key={col.key} className="text-black font-bold text-xs uppercase p-0 border-r border-gray-300 bg-gray-100 h-auto">
                                    <div className="flex flex-col h-full justify-between w-full min-w-[100px]">
                                        <div
                                            className="flex items-center justify-between p-2 cursor-pointer hover:bg-black/5"
                                            onClick={() => handleSort(col.key)}
                                        >
                                            <span className="mr-1 whitespace-nowrap">{col.label}</span>
                                            <ArrowUpDown className="h-3 w-3 text-gray-500" />
                                        </div>
                                        <div className="px-1 pb-1">
                                            <input
                                                className="w-full text-[10px] p-1 border border-gray-300 rounded text-black bg-white focus:outline-none"
                                                placeholder="Cauta..."
                                                value={filters[col.key] || ''}
                                                onChange={e => handleFilterChange(col.key, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedData.map((row, i) => (
                            <TableRow key={i} className="border-white/10 hover:bg-white/5">
                                {visibleColumns.client && <TableCell className="text-gray-300 font-medium border-r border-white/10 p-2 text-sm">{row.client}</TableCell>}

                                {/* Agent Initial: Green if null (New Client) contextually, but actually Logic is:
                                    If Agent P1 is missing, it's a NEW client -> Style differently?
                                    User req: "pentru clientii fara agent initial, se coloreaza celula cu verde si trece valoarea 'client nou'"
                                */}
                                {visibleColumns.agent_p1 && (
                                    <TableCell className={`border-r border-white/10 p-2 text-sm ${!row.agent_p1 ? 'bg-green-500/20 text-green-400 font-bold' : 'text-gray-300'}`}>
                                        {row.agent_p1 || 'Client nou'}
                                    </TableCell>
                                )}

                                {/* Agent Final: Red if null (Lost Client)
                                    User req: "pentru clientii care nu au agent final se coloreaza celula cu rosu si se scrie client pierdut"
                                */}
                                {visibleColumns.agent_p2 && (
                                    <TableCell className={`border-r border-white/10 p-2 text-sm ${!row.agent_p2 ? 'bg-red-500/20 text-red-400 font-bold' : 'text-gray-300'}`}>
                                        {row.agent_p2 || 'Client pierdut'}
                                    </TableCell>
                                )}

                                {visibleColumns.sales_2024 && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm">{formatMoney(row.sales_2024)}</TableCell>}
                                {visibleColumns.sales_2025 && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm">{formatMoney(row.sales_2025)}</TableCell>}
                                {visibleColumns.sales_diff && (
                                    <TableCell
                                        className={`text-right font-mono font-bold p-2 text-sm border-r border-white/10 ${row.sales_diff > 0 ? 'bg-green-500/20 text-green-400' : (row.sales_diff < 0 ? 'bg-red-500/20 text-red-400' : 'text-gray-400')}`}
                                    >
                                        {formatMoney(row.sales_diff)}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {processedData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-gray-500 py-8">Nu exista date.</TableCell>
                            </TableRow>
                        )}

                        {/* Total Row */}
                        {processedData.length > 0 && (
                            <TableRow className="bg-white/10 border-t border-white/20 font-bold">
                                {visibleColumns.client && <TableCell className="text-white p-2">TOTAL</TableCell>}
                                {visibleColumns.agent_p1 && <TableCell className="text-white p-2 text-center">-</TableCell>}
                                {visibleColumns.agent_p2 && <TableCell className="text-white p-2 text-center">-</TableCell>}
                                {visibleColumns.sales_2024 && <TableCell className="text-right text-white font-mono p-2">{formatMoney(totalSales2024)}</TableCell>}
                                {visibleColumns.sales_2025 && <TableCell className="text-right text-white font-mono p-2">{formatMoney(totalSales2025)}</TableCell>}
                                {visibleColumns.sales_diff && (
                                    <TableCell className={`text-right font-mono p-2 ${totalSalesDiff > 0 ? 'text-green-400' : (totalSalesDiff < 0 ? 'text-red-400' : 'text-gray-300')}`}>
                                        {formatMoney(totalSalesDiff)}
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-white/5 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                        Pagina {currentPage} din {totalPages} ({processedData.length} inregistrari)
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            Urmator
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function RetainedClientsTable({ data, table1, table2 }) {
    const [sortConfig, setSortConfig] = useState({ key: 'agent', direction: 'asc' });
    const [filters, setFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState({
        agent: true,
        clients_p1: true,
        clients_p2: true,
        retained_count: true,
        retention_rate: true
    });
    const [currentPage, setCurrentPage] = useState(1);
    const ROWS_PER_PAGE = 15;

    const formatPercent = (val) => {
        if (!val || isNaN(val)) return "0.00%";
        return (val * 100).toFixed(0) + "%";
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const getProcessedData = () => {
        if (!data) return [];
        let filtered = [...data];

        // Filter
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                filtered = filtered.filter(row => {
                    const val = row[key];
                    if (val === null || val === undefined) return false;
                    return String(val).toLowerCase().includes(filters[key].toLowerCase());
                });
            }
        });

        // Sort
        filtered.sort((a, b) => {
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];

            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        });

        return filtered;
    };

    const processedData = getProcessedData();
    const totalPages = Math.ceil(processedData.length / ROWS_PER_PAGE);
    const paginatedData = processedData.slice(
        (currentPage - 1) * ROWS_PER_PAGE,
        currentPage * ROWS_PER_PAGE
    );

    const handleExport = async () => {
        try {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(processedData.map(row => ({
                "Agent": row.agent,
                "Clienti 2024": row.clients_p1,
                "Clienti 2025": row.clients_p2,
                "Clienti Comuni 2024-2025": row.retained_count,
                "Rata pastrare 2025": formatPercent(row.retention_rate)
            })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Clienti Pastrati");
            XLSX.writeFile(wb, "Clienti_Pastrati.xlsx");
        } catch (error) {
            console.error("Export failed:", error);
            toast.error("Export failed");
        }
    };

    const columns = [
        { key: 'agent', label: 'Agent' },
        { key: 'clients_p2', label: 'Clienti 2025' },
        { key: 'clients_p1', label: 'Clienti 2024' },
        { key: 'retained_count', label: 'Clienti Comuni 2024-2025' },
        { key: 'retention_rate', label: 'Rata pastrare 2025' }
    ];

    return (
        <Card className="bg-[#18181b] border-white/10 overflow-hidden mt-8">
            <div className="bg-yellow-400/90 p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-black uppercase">
                    Clienți păstrați per agent (2025 vs 2024)
                </h2>
                <div className="flex items-center space-x-2">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="bg-black/10 text-black border-black/20 hover:bg-black/20">
                                <Settings className="h-4 w-4 mr-2" /> Coloane
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 p-2" align="end">
                            <div className="space-y-1">
                                {columns.map(col => (
                                    <div
                                        key={col.key}
                                        className="flex items-center space-x-2 p-2 hover:bg-white/5 rounded cursor-pointer"
                                        onClick={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                                    >
                                        <div className={`w-4 h-4 border rounded flex items-center justify-center ${visibleColumns[col.key] ? 'bg-green-500 border-green-500' : 'border-gray-500'}`}>
                                            {visibleColumns[col.key] && <Check className="w-3 h-3 text-black" />}
                                        </div>
                                        <span className="text-sm text-gray-200">{col.label}</span>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                    <Button onClick={handleExport} size="sm" className="bg-black/80 hover:bg-black text-white border border-white/20">
                        <Download className="w-4 h-4 mr-2" />
                        Export XLSX
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto min-h-[300px]">
                <Table>
                    <TableHeader className="bg-gray-100">
                        <TableRow>
                            {columns.map(col => visibleColumns[col.key] && (
                                <TableHead key={col.key} className="text-black font-bold text-xs uppercase p-0 border-r border-gray-300 bg-gray-100 h-auto">
                                    <div className="flex flex-col h-full justify-between w-full min-w-[100px]">
                                        <div
                                            className="flex items-center justify-between p-2 cursor-pointer hover:bg-black/5"
                                            onClick={() => handleSort(col.key)}
                                        >
                                            <span className="mr-1 whitespace-nowrap">{col.label}</span>
                                            <ArrowUpDown className="h-3 w-3 text-gray-500" />
                                        </div>
                                        <div className="px-1 pb-1">
                                            <input
                                                className="w-full text-[10px] p-1 border border-gray-300 rounded text-black bg-white focus:outline-none"
                                                placeholder="Cauta..."
                                                value={filters[col.key] || ''}
                                                onChange={e => handleFilterChange(col.key, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedData.map((row, i) => (
                            <TableRow key={i} className="border-white/10 hover:bg-white/5">
                                {visibleColumns.agent && <TableCell className="text-gray-300 font-medium border-r border-white/10 p-2 text-sm">{row.agent}</TableCell>}
                                {visibleColumns.clients_p1 && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm">{row.clients_p1}</TableCell>}
                                {visibleColumns.clients_p2 && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm">{row.clients_p2}</TableCell>}
                                {visibleColumns.retained_count && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm">{row.retained_count}</TableCell>}
                                {visibleColumns.retention_rate && <TableCell className="text-right text-white font-mono border-r border-white/10 p-2 text-sm font-bold">{formatPercent(row.retention_rate)}</TableCell>}
                            </TableRow>
                        ))}
                        {processedData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-gray-500 py-8">Nu exista date.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-white/5 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                        Pagina {currentPage} din {totalPages} ({processedData.length} inregistrari)
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            Urmator
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}
